import { cloneDeep, isEmpty } from "lodash";
import moment from "moment";
import { tz } from "moment-timezone";
import dealutil from "@dotpe/rista-library/lib/sale/deal_util.js";
import { businessHelpers, catalogHelpers } from "@dotpe/rista-pos-common";
import bill from "@dotpe/rista-library/lib/bill/bill.js";
import saleutil from "@dotpe/rista-library/lib/sale/sale_util.js";
import chargeutil from "@dotpe/rista-library/lib/sale/charge_util.js";
import store from "../../redux-store";
import { calculateAndSaveSaleData, getAllDiscounts, getClonedSaleDataFromReducer, getSaleItems, populateMetaInfo } from "../saleHelpers";
import { authenticateCustomerData, resetCustomerStore } from "../../redux-store/slices/CustomerSlice";
import { notifyError, printSaleTickets } from "../../effects-bridge";
import { fetchCommonData } from "../../redux-store/slices/common";
import { saveSaleData } from "../../redux-store/slices/SaleSlice";
import { ENDPOINT } from "../../config/endpoints";
import { METHODS } from "../../data/constants";
import { REST_SALE_BACK_ITEMS_NOT_PROCESSED } from "../../data/constants";
import { backendErrorMap } from "../../utils/backendErrors";
import { LICENCE_CODES } from "../../commonComponents/LeftMenu/menuItemsData";
import type { SaleItem } from "../../models/saleItem";
import { convertArrayToObject, featureRestrictions } from "../commonHelpers";
import { BILL_SETTLEMENT, CLOSE_SALE_BALANCE } from "../../utils/restrictionConstants";
import type { DiscountApprovalType } from "../../redux-store/slices/DiscountSlice";

/**
 * Uplifted pieces of `helper/saleHelpers` (apps/web + apps/mobile).
 *
 * Seeded with `showToastFromApi` — the only symbol apiService needs from this ~1300-line file.
 * The rest (sale maths, `getBasicSaleData`, `getParamsForSaleCalculate`, ...) stays in the apps
 * for now; `SaleSlice`'s uplift will extend this folder.
 *
 * ⚠️ Both intra-core imports here are BARE FOLDER imports, and that is CORRECT even though both
 * targets are platform-divergent (ledger hazard 16's table): a `shared.ts` runs on BOTH
 * platforms, so it must let the bundler pick the variant. Naming a variant here would give one
 * platform the other's error strings.
 */

/**
 * `jshashes` — required, not imported, because it ships no type declarations and both apps
 * already reach it exactly this way. Only `MD5.hex_hmac` is used, by `verifyOtpHash`.
 */
// eslint-disable-next-line @typescript-eslint/no-require-imports
const Hashes = require("jshashes");

type ErrorMap = typeof backendErrorMap;

/**
 * Maps a backend error code to a human message and shows it as an error toast, unless the code
 * is one of the three deliberately silent ones.
 *
 * SHARED as of 2026-08-19, under standing rule 2(a). The two apps' bodies differed in exactly
 * two ways, neither of them behaviour:
 *  - web referenced the CONSTANT `REST_SALE_BACK_ITEMS_NOT_PROCESSED` where mobile inlined the
 *    identical string literal — same value, so core uses the constant;
 *  - mobile also ran `console.log('showToastFromApi== mess', message)` — dead debug output,
 *    which was then the ONLY remaining difference, so it is removed and the body shared.
 *
 * The toast itself is the injected `notifyError`, because it is UI and core cannot import
 * `@rista/ui` — web wires `ToastNotifyError`, mobile its React Native equivalent.
 */
export function showToastFromApi(code: string) {
  const errorCodeNotToShow = [
    REST_SALE_BACK_ITEMS_NOT_PROCESSED,
    "REST_ERR_AGGREGATOR_CUSTOM",
    "ERR_ITEM_RECOMMENDATION_LICENCE_INACTIVE",
  ];
  //get error string from map, otherwise show error code
  if (!errorCodeNotToShow?.includes(code)) {
    let message = backendErrorMap[code as keyof ErrorMap] || code;
    notifyError(message);
  }
}

/**
 * Does any sale item carry errors?
 *
 * **Byte-identical in both apps.**
 *
 * 🔴 **It ALWAYS RETURNS `false`.** The `.map` callback's `return true` returns from the
 * CALLBACK, not from `hasItemLeveErrors`, and the mapped array is discarded — so the function
 * falls through to `return false` no matter what. Both apps have shipped this bug for as long as
 * the file has existed, and every caller therefore behaves as if items never have errors.
 *
 * **Preserved verbatim, deliberately.** Fixing it here would silently switch on error handling
 * in checkout, sale and tableView across both platforms at once — a behaviour change well
 * outside an uplift. Recorded as a hazard-17-class finding; it wants its own ticket.
 * (The misspelling "Leve" is the apps' own and is kept so call sites need no edit.)
 */
export function hasItemLeveErrors(saleItems: any = []) {
  if (saleItems?.length) {
    saleItems.map((itemData: any) => {
      if (itemData.errors?.length) {
        return true;
      }
    });
  }
  return false;
}

/**
 * Clear the customer attached to the current sale. Identical in both apps.
 *
 * Reads/writes app state through core's own store (the 2026-08-21 decision).
 */
export function resetCustomer() {
  store.dispatch(resetCustomerStore());
}

/**
 * True when no ticket on the sale is still `Submitted` — i.e. the KOT being acted on is the last
 * outstanding one.
 *
 * ⚠️ **Byte-identical in both apps** apart from quote style (diffed, not assumed), so it is
 * shared. Note the early `break` makes it "is there NO submitted ticket", not "is this the last
 * index"; the name reads the other way round and is the apps' own.
 */
export function isLastKOT(KOTs: any = []) {
  let flag = true;
  for (let i = 0; i < KOTs?.length; i++) {
    if (KOTs[i]?.ticketStatus === "Submitted") {
      flag = false;
      break;
    }
  }
  return flag;
}

/**
 * Is the signed-in user on the ordering-only licence (POO)?
 *
 * SHARED: the two apps' bodies are the same expression, differing only in line wrapping — the
 * one returns it on a single line, the other parenthesises it across three. `--verify`-class
 * formatting, not behaviour.
 *
 * ⚠️ `LICENCE_CODES` is a BARE FOLDER import, which is what a `shared.ts` must do (hazard 16's
 * table) — and it is safe here for the stronger reason too: the constant lives in that folder's
 * own `shared.ts`, so both platforms resolve to the same object either way.
 */
export function isOrderingLicense() {
  return store.getState()?.businessStore?.userData?.assignedLicence === LICENCE_CODES.POO;
}

/**
 * The channel the order screen has selected. Web's body; mobile's reaches the same store path
 * through `saleHelpers.getSelectedChannelDataCommon(store)` (rista-pos-common), so both apps
 * re-export this one.
 */
export function getSelectedChannel() {
  return store.getState()?.saleDataStore?.selectedChannelData;
}

/**
 * May this user close / settle the sale in the store? WEB's body, moved with the feature gate
 * (`helpers/featureRestrictionHelper`: Close and Quick Settle).
 *
 * ⚠️ It reads the sale in the STORE (`getClonedSaleDataFromReducer`), not the order a caller is
 * looking at — as it did on web. Mobile keeps its own version, which takes `isBottomCta` as its
 * FIRST argument, for its non-adopted screens.
 */
export function showCloseSaleCta(
  currentDiscountApprovalData = {} as DiscountApprovalType,
  isBottomCta = true,
  isBackorder = false
) {
  // `any` only because core's `PaymentInfo` model omits `balanceAmount`, which web's looser types allowed
  let saleData: any = getClonedSaleDataFromReducer();
  return (
    featureRestrictions(BILL_SETTLEMENT) &&
    (!isBottomCta && (saleData?.paymentInfo?.balanceAmount > 0 && !isBackorder)
      ? featureRestrictions(CLOSE_SALE_BALANCE)
      : true) &&
    // hideCloseOptionForDiscountApproval(currentDiscountApprovalData) &&
    !isOrderingLicense()
  );
}

/*
 * `updateChannelDataInSale` was uplifted here and then REMOVED when DP-7554 merged in.
 *
 * `channel`'s `applyChannelToSale(sale, channel, mode)` replaces it — and also
 * replaces `saveChannleDataInSale`, which is the point: the POS picks between those two
 * with an `if/else if` far from either, and the destructive one is the default. Read that
 * file's header for the three behavioural corrections it makes; the one that matters most
 * is that `reset` clears the customer UNCONDITIONALLY, where this version guarded on
 * `serviceLabel` being non-empty and so kept a delivery customer attached across a switch.
 *
 * It is also pure, which is why it can serve both platforms: this version called
 * `getClonedSaleDataFromReducer`, and that is DESKTOP-ONLY.
 *
 * The APPS keep their own copies under the as-is policy. Do not re-uplift this one.
 */

/**
 * Stamp a sale for OFFLINE persistence: give it an offline number if it has neither a bill number
 * nor one already, run it through the platform's `populateMetaInfo`, and mark it
 * `SAVE_SALE` / `tobesynced`.
 *
 * SHARED: the two app bodies are identical modulo `_.isEmpty` vs `isEmpty` (the same lodash
 * function, imported differently) and one pair of parentheses around `new Date()`.
 *
 * 🔴 **The `populateMetaInfo` import is the folder, not a variant, and that is deliberate** — it
 * is the one thing in this function that still differs per platform (the two sides clone
 * differently and stamp different fields), so the bundler must pick the variant. This is
 * the sanctioned "a shared.ts may import a platform-split folder" exception, applied to this
 * module's OWN folder; it makes `shared.ts -> index.<platform>.ts -> shared.ts` a cycle, which is
 * safe here ONLY because the call happens at call time and never at module-eval time. **Do not
 * move a `populateMetaInfo()` call to module scope in this file.**
 *
 * ⚠️ `syncStatus` is the string literal `"tobesynced"` in both apps here, even though
 * `SALE_SYNC_STATUSES.TO_BE_SYNCED` exists and is used two functions away. Transcribed as-is.
 */
export function updateSaleMetaInfo(saleData: any) {
  const now = new Date().getTime().toString();

  if (isEmpty(saleData.billNumber) && isEmpty(saleData.offlineNumber)) {
    saleData.offlineNumber = now.substr(2, 8);
  }
  saleData = populateMetaInfo(saleData);
  saleData.operation = "SAVE_SALE";
  saleData.syncStatus = "tobesynced";
  return saleData;
}

/**
 * The channel definition for a channel id, resolved to this branch's VARIANT when the channel has
 * variants.
 *
 * SHARED: the two app bodies differ only in a type annotation on the local, a `!!` on a length
 * check that is already truthy-tested, and brace/comment style — no token of logic.
 *
 * `catalogHelpers` comes from `@dotpe/rista-pos-common`, which is already a peer dependency of
 * this package and is imported directly rather than bridged (same decision as the checkout saga).
 */
export function getChannelDef(id: string = "") {
  let result: any = {},
    channelDef: any[] = store.getState()?.catalogStore?.channelDef?.length ? store.getState().catalogStore.channelDef : [],
    branchId = store.getState()?.businessStore?.businessData?.branchId;
  if (channelDef.length) {
    let index = channelDef.findIndex((data: any) => data.mid === id);
    if (index > -1) {
      let channelData = channelDef[index];
      if (channelData?.variants?.length)
        channelData = (catalogHelpers as any).handleChannelVariant(channelData, branchId);
      result = { ...channelData };
    }
  }
  return result;
}

/**
 * Create the KOT for an advance order that has just been moved to Process, then print its
 * tickets and put the returned sale in the store.
 *
 * Uplifted 2026-09-15 from web's `helper/saleHelpers` for `myOrders/useOrderActions`. Mobile has
 * no copy — its only call site is COMMENTED OUT in `MyOrderCard.updateAdvanceOrderAction` — so
 * mobile gains it with the shared action layer (plan §3, "web behaviour mobile lacked").
 */
export function handleCreateKOT(saleId: string) {
  if (!saleId) return;
  let updateCallback = function (saleData: any) {
    if (saleData.code) return;
    printSaleTickets(saleData?.sale, saleData?.sale?.tickets);
    store.dispatch(saveSaleData(saleData));
  };
  let payload = { saleId };
  store.dispatch(
    fetchCommonData({
      method: METHODS.POST,
      body: payload,
      url: ENDPOINT.MYORDER.createTicket(saleId),
      callback: updateCallback,
    })
  );
}

/**
 * The printable bill as HTML — what "View Bill" shows and what the shells hand to their HTML
 * renderer. Uplifted 2026-09-16 for my-orders v2 (`sheets/ViewBill`). Web's body; mobile's was the
 * same over a lodash clone plus a stray log. The library is IMPORTED (CLAUDE.md), where the apps
 * read `window.RL.bill` / `globalThis.bill` — the same code reached two ways.
 */
export function getBillHtml(saleData: any): string {
  const businessData = store.getState().businessStore?.businessData;
  const saleWithBillTemplateConfig = { ...structuredClone(saleData), showPhoneNumber: businessData?.billTemplate?.showPhoneNumber };
  return bill.getDIV(saleWithBillTemplateConfig, { billTemplate: businessData?.billTemplate });
}

/**
 * The deals currently live for the cart's channel, and the deals that apply to the sale as
 * priced. Both delegate to `dealutil`; the try/catch is the apps' own — a merchant with a
 * malformed discount must not take the till down, so an unreadable rule set degrades to
 * "no offers".
 *
 * ⚠️ SHARED, not split, even though both apps read `dealutil` off a DIFFERENT global —
 * `window.RL.dealutil` on web, `globalThis.dealutil` on mobile. That difference is the app
 * bootstrap's, not the logic's: once the module is IMPORTED (CLAUDE.md's rista-library rule,
 * audit check K) the two bodies are identical token for token, so splitting them here would
 * manufacture a per-platform fork out of one function.
 *
 * The two timestamps really are built differently — `tz(zone)` for the live deals, and
 * `moment().tz(zone || "Asia/Calcutta")` for the sale's — in BOTH apps. That asymmetry is
 * carried over verbatim rather than tidied: `getCurrentDeals` deliberately passes an invalid
 * date when the branch has no zone, and the fallback zone on the other side changes which
 * deals a zoneless branch sees.
 */
export function getCurrentDeals() {
  try {
    const state = store.getState();
    const catalogStore = state.catalogStore,
      saleDataStore = state.saleDataStore,
      saleData = saleDataStore.saleData,
      selectedChannelData = saleDataStore.selectedChannelData,
      discountList = catalogStore?.discounts && Object.values(catalogStore.discounts),
      membershipId = (saleData.customerInfo || {}).membershipId,
      businessData = state.businessStore?.businessData;
    let data: any = dealutil.getCurrentDeals({
      discounts: discountList,
      branchTimes: [tz(businessData?.timeZone?.zoneId).format()],
      branchId: businessData.branchId,
      membershipId: membershipId,
      channelDefId: selectedChannelData.channelDefId,
      transactionEnd: businessData.transactionEnd,
    });
    data = data.length ? data : [];
    return data;
  } catch (error) {
    return [];
  }
}

export function getCurrentDealsForSale() {
  try {
    const state = store.getState();
    const catalogStore = state.catalogStore,
      saleDataStore = state.saleDataStore,
      saleData = saleDataStore.saleData,
      businessData = state.businessStore?.businessData,
      discountList = catalogStore?.discounts && Object.values(catalogStore.discounts);

    return dealutil.getCurrentDealsForSale({
      discounts: discountList,
      branchTime: moment()
        .tz(businessData?.timeZone?.zoneId || "Asia/Calcutta")
        .format(),
      sale: saleData,
      transactionEnd: businessData.transactionEnd,
    });
  } catch (error) {
    return [];
  }
}

/**
 * The distinct `itemId`s currently in the cart.
 *
 * SHARED under standing rule 2(a): the two apps' bodies differed only in that web defaulted the
 * parameter to `[]` and re-guarded with `Array.isArray`, where mobile assumed an array and threw
 * on `undefined`. The defensive web body wins — it is a strict superset of mobile's behaviour on
 * every input mobile could already handle.
 *
 * Used by the Suggestive Selling flow to drop recommendations for items already in the cart, and
 * by anything else that needs "is this item on the bill".
 */
export function getUniqueCartItemIds(saleItems: SaleItem[] = []): Set<string> {
  const safeSaleItems = Array.isArray(saleItems) ? saleItems : [];
  return new Set(safeSaleItems.map((item) => item.itemId));
}

/* ───────────────────────── customer-authentication OTP ───────────────────────── */

/**
 * Ask the backend to mint an OTP for the customer on this bill.
 *
 * The answer does NOT come back here: the saga writes an MD5 hash of it onto
 * `customerStore.customerAuthenticationData.otpHash`, and `verifyOtpHash` compares against that.
 * The plaintext code only ever exists on the customer's phone — which is why verification is a
 * local hash comparison rather than a second round trip.
 *
 * SHARED under standing rule 2(a): the two apps' bodies are identical apart from how each
 * reached its own `getClonedSaleDataFromReducer`, which this file resolves through the FOLDER
 * import below so each platform still gets its own.
 */
export function fetchCustomerOtpHash() {
  const branchId = store.getState().businessStore?.businessData?.branchId || "";
  const saleData = getClonedSaleDataFromReducer();
  const customerRefId = saleData?.customerInfo?.customerRefId || "";
  store.dispatch(authenticateCustomerData({ branchId, customerRefId }));
}

/**
 * Check a typed OTP and, if it matches, mark the customer verified on the bill.
 *
 * ⚠️ **It WRITES on success** — `customerInfo.authenticated = true`, then reprices and saves.
 * That is not a side effect to factor out: `authenticated` is the flag every
 * authentication-gated discount reads (`needsCustomerAuthentication`), so a verification that
 * did not reach the sale would leave the discount blocked by the check it had just satisfied.
 *
 * Returns whether the code matched, so a caller can show "incorrect" without re-deriving it.
 *
 * ⚠️ The hash is an MD5 HMAC keyed on the customer's own `customerRefId`. A sale with no
 * customer attached therefore keys on `""` and can never match — collect the number first.
 */
export function verifyOtpHash(otp: string): boolean {
  const MD5 = new Hashes.MD5();
  // `any` because the two platforms' `getClonedSaleDataFromReducer` return different types
  // (`SaleDataType` on mobile, untyped on web) and only mobile's narrows `customerInfo` to
  // optional. The apps' own bodies were untyped here.
  const saleData: any = getClonedSaleDataFromReducer();
  const otpHash = store.getState().customerStore?.customerAuthenticationData?.otpHash || "";
  const customerRefId = saleData?.customerInfo?.customerRefId || "";

  if (otpHash !== MD5.hex_hmac(customerRefId, otp)) return false;

  saleData.customerInfo["authenticated"] = true;
  calculateAndSaveSaleData(saleData);
  return true;
}

/**
 * Strip these discounts off the bill and reprice — the "continue without the discount" answer to
 * an authentication prompt the customer cannot or will not complete.
 *
 * ⚠️ Removes from BOTH lists by `discountId`: `billDiscountList` is what the engine prices from,
 * and `discountList` is the priced result the UI reads. Dropping only the first leaves a stale
 * priced row on screen until the next recalculation.
 *
 * 🔴 This is the LOCAL strip only. It does not release a partner coupon — for anything with a
 * `couponProvider`, use `packages/core/discounts`' `removeDiscount`, which pairs the two.
 *
 * ⚠️ **RETURNS the repriced sale**, which is MOBILE's shape — web's body returned nothing. The
 * return is a strict superset: web's two callers ignore it, and mobile's `CustomerVerificationPopup`
 * feeds it straight to `updateTableData`. Dropping it would break that call site; adding it
 * changes nothing on web.
 *
 * 🔴 **`cloneDeep`, NOT `structuredClone`** — Hermes has none, apps/mobile polyfills it at boot,
 * and a SHARED file that leaned on that would depend on one app's bootstrap order while every
 * node-run test passed. `catalog/catalogModel.ts` is where this rule is written down.
 * Not `commonHelpers.deepClone` either: that is a JSON round trip and drops `undefined` keys.
 * Web's body used `structuredClone` and mobile's `JSON.parse(JSON.stringify(...))`; `cloneDeep`
 * matches web's semantics and is a superset of mobile's.
 *
 * Otherwise SHARED under standing rule 2(a): the apps also differed in `!!data.length` vs
 * `data.length`, which is not behaviour.
 */
export function removeDiscountFromSale(data: any[] = []) {
  const saleData: any = cloneDeep(store.getState().saleDataStore.saleData);
  const billDiscountList = [...(saleData.billDiscountList || [])];
  const discountList = [...(saleData.discountList || [])];

  (data || []).forEach((discount: any) => {
    const billIndex = billDiscountList.findIndex(
      (entry: any) => entry.discountId === discount.discountId,
    );
    if (billIndex > -1) billDiscountList.splice(billIndex, 1);

    const discountIndex = discountList.findIndex(
      (entry: any) => entry.discountId === discount.discountId,
    );
    if (discountIndex > -1) discountList.splice(discountIndex, 1);
  });

  saleData.billDiscountList = billDiscountList;
  saleData.discountList = discountList;
  return calculateAndSaveSaleData(saleData);
}

/**
 * Whether the sale in the store needs its customer verified before it is printed or checked out:
 * the business asks for it and a discount on the sale authenticates the customer, which the
 * customer has not done yet (`saleutil.isCustomerAuthenticationRequired`).
 *
 * Uplifted 2026-09-29 for My Orders v2's detail, which now asks this itself. The two apps' bodies
 * were the same check; they differed only in how they read the sale (web cloned it, mobile's
 * reader returned a blank sale when there was none), and the library check only reads it, with a
 * blank sale answering false either way. A throw is logged and answers false, as in both.
 */
export function isDiscCustAuthReq(): boolean {
  if (!businessHelpers.isAuthenticationEnabled(store.getState().businessStore?.businessData)) return false;
  try {
    return !!saleutil.isCustomerAuthenticationRequired(store.getState().saleDataStore?.saleData || {});
  } catch (error) {
    console.error("RL-error", error);
    return false;
  }
}

// ── charges (uplifted 2026-09-29 for My Orders v2's sale return and its charges editor) ────────
// WEB's bodies on both platforms (web supersedes mobile), both apps re-export them. Mobile's
// copies differed only in plumbing and cloning: its `getParamsForEditSaleCalculate` handed the
// caller's charges to `charge_util` UNcloned (the library writes `charge.scope` on them), deep-
// cloned the whole catalog store instead, and read `catalogStore?.categoryGroup.length` without a
// guard; an empty `taxArea` object went through as `[]` there and as `{}` here. Mobile had no
// `handleLocalCharges`.

/** The catalog's optional order-level charges for the store's selected channel and this branch. */
export function getApplicableCharges(): any[] {
  try {
    let allCharges: any = store.getState().catalogStore.charges || {};
    const channel = store.getState().saleDataStore?.selectedChannelData,
      branchId = store.getState().businessStore?.businessData?.branchId;
    allCharges = (allCharges && structuredClone(Object.values(allCharges))) || [];
    return chargeutil.filterApplicableCharges({
      charges: allCharges,
      channelDefId: channel.channelDefId,
      branchId: branchId,
      applyAt: "Order",
      scope: "Optional",
    });
  } catch (error) {
    return [];
  }
}

/** The pricing params for a sale whose charges were edited — `charges` are the editor's candidates. */
export function getParamsForEditSaleCalculate(saleData: any, charges: any) {
  const catalogStore = store.getState().catalogStore,
    businessStore = store.getState().businessStore;
  return {
    // charge objects are mutated by charge_util (charge.scope is written) — clone required
    charges: charges ? structuredClone(charges) : [],
    // all other catalog params are read-only inside the library (filter/find only)
    bizBrands: catalogStore?.bizBrandsData || [],
    billRoundingMode: businessStore.businessData?.roundingMode ? businessStore.businessData.roundingMode : "",
    branchTime: businessStore.businessData?.timeZone?.zoneId
      ? moment().tz(businessStore.businessData.timeZone.zoneId).format("YYYY-MM-DDTHH:MM:SS")
      : moment().format("YYYY-MM-DDTHH:MM:SS"),
    brands: catalogStore?.brandsData || [],
    categories: catalogStore.categoryData ? Object.values(catalogStore.categoryData) : [],
    categoryGroups: catalogStore?.categoryGroup || [],
    channelDefs: catalogStore.channelDef || [],
    channelvos: catalogStore.channelvos || [],
    discounts: structuredClone(getAllDiscounts()),
    items: structuredClone(getSaleItems(saleData.saleItems)),
    taxArea: catalogStore.taxArea || [],
    taxTypes: catalogStore.taxTypes || [],
    transactionEnd: [],
    roundOnlyCashPaymentMode: businessStore?.businessData?.enableRoundingCashOnly || false,
  };
}

/** The editor's charges with the rate the store's sale already applies, where it applies one. */
export function handleLocalCharges(parentChargesList: any[]): any[] {
  const saleData = getClonedSaleDataFromReducer();
  const chargeDataFromSale = convertArrayToObject(saleData.chargeList || [], "chargeId"); //if there are applied charges in sale, use that chargeRate
  return parentChargesList.map((charge: any) =>
    chargeDataFromSale[charge?.mid] ? { ...charge, chargeRate: chargeDataFromSale[charge?.mid]?.chargeRate } : charge
  );
}
